import { useEffect, useRef, useState } from "react";
import { Stream } from "@cloudflare/stream-react";
import { toast } from "sonner";
import { Plus, Trash, VideoCamera, Pause, Play, Copy } from "@phosphor-icons/react";

/**
 * BoxAnnotator — draw bounding boxes directly on a Cloudflare Stream video.
 *
 * Interaction model:
 *   1. Play/scrub the video to the frame you want to annotate.
 *   2. Click "+ Add box at current time" to spawn a 20%×20% draft box at the
 *      current playback time.
 *   3. Drag the box to reposition, drag its bottom-right handle to resize.
 *   4. Edit label / confidence in the inline row.
 *   5. When you save the demo, `boxes` is emitted as the fractional
 *      `{t,x,y,w,h,label,confidence}` array the backend expects.
 *
 * Fractional coordinates (0..1) — same as the API + LiveVision overlay,
 * so the annotator is truly WYSIWYG.
 */
export default function BoxAnnotator({ videoUrl, boxes, onChange }) {
  const wrapRef = useRef(null);
  const streamPlayerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drag, setDrag] = useState(null); // { id, mode: 'move'|'resize', startX, startY, origX, origY, origW, origH }
  const [windowSize, setWindowSize] = useState(0.4); // ± seconds — which boxes to draw

  // ---- Cloudflare Stream UID detection so we can query the sibling <video>
  const uidMatch = String(videoUrl || "").match(/cloudflarestream\.com\/([a-f0-9]{32})/i);
  const uid = uidMatch ? uidMatch[1] : String(videoUrl || "").trim();

  // Poll the underlying <video> once mounted so we can read currentTime.
  useEffect(() => {
    if (!uid || !wrapRef.current) return;
    let raf;
    const tick = () => {
      const v = wrapRef.current?.querySelector("video");
      if (v) {
        setCurrentTime(v.currentTime);
        setDuration(v.duration || 0);
        setPlaying(!v.paused);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [uid]);

  const togglePlay = () => {
    const v = wrapRef.current?.querySelector("video");
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };

  const seek = (t) => {
    const v = wrapRef.current?.querySelector("video");
    if (v) v.currentTime = Math.max(0, Math.min(duration || 0, t));
  };

  const addBox = () => {
    const nb = {
      _id: Math.random().toString(36).slice(2),
      t: parseFloat(currentTime.toFixed(2)),
      x: 0.4, y: 0.4, w: 0.2, h: 0.2,
      label: "defect",
      confidence: 0.9,
    };
    onChange([...(boxes || []), nb]);
    setSelected(nb._id);
  };

  const updateBox = (id, patch) => {
    onChange((boxes || []).map((b) => ((b._id || `t${b.t}`) === id ? { ...b, ...patch } : b)));
  };

  const deleteBox = (id) => {
    onChange((boxes || []).filter((b) => (b._id || `t${b.t}`) !== id));
    if (selected === id) setSelected(null);
  };

  const copyJson = () => {
    // Strip UI-only _id before copying
    const clean = (boxes || []).map(({ _id, ...rest }) => rest);
    navigator.clipboard.writeText(JSON.stringify(clean, null, 2))
      .then(() => toast.success("Boxes JSON copied"))
      .catch(() => toast.error("Copy failed"));
  };

  // Boxes visible at current time (±windowSize seconds).
  const visible = (boxes || []).filter((b) => Math.abs((b.t ?? 0) - currentTime) <= windowSize);

  // ---- Pointer handlers on the overlay ----
  const onPointerDown = (e, box, mode) => {
    e.stopPropagation();
    const rect = wrapRef.current.querySelector("[data-video-rect]").getBoundingClientRect();
    setDrag({
      id: box._id || `t${box.t}`,
      mode,
      startPx: { x: e.clientX, y: e.clientY },
      rect,
      orig: { x: box.x, y: box.y, w: box.w, h: box.h },
    });
    setSelected(box._id || `t${box.t}`);
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dx = (e.clientX - drag.startPx.x) / drag.rect.width;
      const dy = (e.clientY - drag.startPx.y) / drag.rect.height;
      if (drag.mode === "move") {
        updateBox(drag.id, {
          x: clamp01(drag.orig.x + dx, 0, 1 - drag.orig.w),
          y: clamp01(drag.orig.y + dy, 0, 1 - drag.orig.h),
        });
      } else {
        updateBox(drag.id, {
          w: clamp01(drag.orig.w + dx, 0.02, 1 - drag.orig.x),
          h: clamp01(drag.orig.h + dy, 0.02, 1 - drag.orig.y),
        });
      }
    };
    const onUp = () => setDrag(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    // eslint-disable-next-line
  }, [drag]);

  if (!uid) {
    return (
      <div className="border border-dashed border-[#2A2A2A] p-6 text-center text-[#8E8E93] text-sm" data-testid="box-annotator-empty">
        Upload a video first — the annotator needs a Cloudflare Stream source to overlay boxes.
      </div>
    );
  }

  return (
    <div data-testid="box-annotator">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <VideoCamera size={14} color="#DFFF00" />
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">Visual annotator</div>
          <div className="font-mono text-[10px] text-[#8E8E93]">·</div>
          <div className="font-mono text-[10px] text-[#8E8E93]">
            {currentTime.toFixed(2)}s / {duration ? duration.toFixed(2) : "—"}s · showing {visible.length} of {(boxes || []).length}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={togglePlay} className="btn-ghost text-[10px] py-1 px-2" data-testid="ba-toggle">
            {playing ? <><Pause size={12} weight="fill" /> Pause</> : <><Play size={12} weight="fill" /> Play</>}
          </button>
          <button type="button" onClick={addBox} className="btn-primary text-[10px] py-1 px-2" data-testid="ba-add">
            <Plus size={12} /> Add box at {currentTime.toFixed(1)}s
          </button>
          <button type="button" onClick={copyJson} className="btn-ghost text-[10px] py-1 px-2" title="Copy JSON to clipboard" data-testid="ba-copy">
            <Copy size={12} /> JSON
          </button>
        </div>
      </div>

      <div ref={wrapRef} className="relative bg-black border border-[#2A2A2A] overflow-hidden" style={{ userSelect: "none" }}>
        <div className="relative" data-video-rect>
          <Stream ref={streamPlayerRef} src={uid} controls={false} responsive muted preload="metadata" playsInline />
          {/* Overlay */}
          <div className="absolute inset-0" onPointerDown={() => setSelected(null)}>
            {visible.map((b) => {
              const id = b._id || `t${b.t}`;
              const isSel = selected === id;
              return (
                <div
                  key={id}
                  onPointerDown={(e) => onPointerDown(e, b, "move")}
                  className="absolute cursor-move"
                  style={{
                    left: `${b.x * 100}%`,
                    top: `${b.y * 100}%`,
                    width: `${b.w * 100}%`,
                    height: `${b.h * 100}%`,
                    border: `2px solid ${isSel ? "#FF4500" : (b.color || "#DFFF00")}`,
                    background: isSel ? "rgba(255,69,0,0.12)" : "rgba(223,255,0,0.06)",
                  }}
                  data-testid={`ba-box-${id}`}
                >
                  <div
                    className="absolute -top-6 left-0 font-mono text-[10px] px-1 whitespace-nowrap"
                    style={{ background: isSel ? "#FF4500" : (b.color || "#DFFF00"), color: "#050505" }}
                  >
                    {b.label} · {Math.round((b.confidence ?? 0.9) * 100)}%
                  </div>
                  <div
                    onPointerDown={(e) => onPointerDown(e, b, "resize")}
                    className="absolute right-0 bottom-0 w-3 h-3 cursor-nwse-resize"
                    style={{ background: "#FF4500" }}
                    data-testid={`ba-box-${id}-resize`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Timeline scrubber */}
      {duration > 0 && (
        <div className="mt-3">
          <input
            type="range"
            min="0"
            max={duration}
            step="0.05"
            value={currentTime}
            onChange={(e) => seek(parseFloat(e.target.value))}
            className="w-full"
            data-testid="ba-scrubber"
          />
          {/* Tick marks for existing boxes so recruiter/admin can scrub to them */}
          <div className="relative h-3 mt-1">
            {(boxes || []).map((b) => {
              const id = b._id || `t${b.t}`;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => seek(b.t)}
                  title={`Seek to ${b.t}s (${b.label})`}
                  className="absolute -top-0.5 w-1 h-3 hover:h-4"
                  style={{
                    left: `${(b.t / duration) * 100}%`,
                    background: selected === id ? "#FF4500" : (b.color || "#DFFF00"),
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Box row editor */}
      <div className="mt-4 border border-[#2A2A2A]">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#0a0a0a] font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">
          <div className="col-span-1">t (s)</div>
          <div className="col-span-3">Label</div>
          <div className="col-span-2">Conf</div>
          <div className="col-span-5">x, y, w, h</div>
          <div className="col-span-1 text-right">Del</div>
        </div>
        {(boxes || []).length === 0 && (
          <div className="px-3 py-8 text-center text-[#555] text-sm">No boxes yet — scrub to a frame and click "Add box".</div>
        )}
        {(boxes || []).map((b) => {
          const id = b._id || `t${b.t}`;
          const isSel = selected === id;
          return (
            <div key={id} className={`grid grid-cols-12 gap-2 px-3 py-2 border-t border-[#2A2A2A] text-xs items-center ${isSel ? "bg-[#FF4500]/[0.06]" : "hover:bg-[#0a0a0a]"}`}>
              <div className="col-span-1">
                <input type="number" step="0.1" min="0" value={b.t} onChange={(e) => updateBox(id, { t: parseFloat(e.target.value) || 0 })} className="w-full bg-transparent border border-[#2A2A2A] px-1 py-0.5 font-mono" />
              </div>
              <div className="col-span-3">
                <input value={b.label || ""} onChange={(e) => updateBox(id, { label: e.target.value })} className="w-full bg-transparent border border-[#2A2A2A] px-2 py-0.5" data-testid={`ba-label-${id}`} />
              </div>
              <div className="col-span-2">
                <input type="number" step="0.01" min="0" max="1" value={b.confidence ?? 0.9} onChange={(e) => updateBox(id, { confidence: parseFloat(e.target.value) || 0 })} className="w-full bg-transparent border border-[#2A2A2A] px-2 py-0.5 font-mono" />
              </div>
              <div className="col-span-5 font-mono text-[10px] text-[#8E8E93]">
                {b.x.toFixed(3)}, {b.y.toFixed(3)}, {b.w.toFixed(3)}, {b.h.toFixed(3)}
              </div>
              <div className="col-span-1 text-right">
                <button type="button" onClick={() => deleteBox(id)} className="text-[#8E8E93] hover:text-[#FF3060]" data-testid={`ba-delete-${id}`}>
                  <Trash size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 font-mono text-[10px] text-[#555]">
        Tip: drag a box to move · drag the orange corner to resize · scrub the timeline to add boxes at other frames.
        Boxes are shown within ±<input type="number" step="0.1" min="0" max="5" value={windowSize} onChange={(e) => setWindowSize(parseFloat(e.target.value) || 0)} className="w-12 bg-transparent border border-[#2A2A2A] px-1 text-center inline-block mx-1" />s of currentTime.
      </div>
    </div>
  );
}

function clamp01(v, min = 0, max = 1) {
  if (Number.isNaN(v)) return min;
  return Math.max(min, Math.min(max, v));
}
